import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  Handle,
  Position,
  ReactFlowProvider,
  useReactFlow,
} from 'react-flow-renderer';
import { Block } from 'baseui/block';
import { Accordion, Panel } from 'baseui/accordion';
import { styled } from 'baseui';
import { useStyletron } from 'baseui';
import yaml from 'js-yaml';
import Prism from 'prismjs';
import 'prismjs/components/prism-yaml';
import 'prismjs/themes/prism.css';
import { FiInfo } from 'react-icons/fi';

import usePipelineSteps from '../../hooks/useTopoSort';

// YAML Schema for custom types
const customRefType = new yaml.Type('!Ref', {
  kind: 'scalar',
  construct: data => data,
});

// Create custom schema using the correct js-yaml API
const CUSTOM_SCHEMA = yaml.DEFAULT_SCHEMA.extend([customRefType]);

const proOptions = { hideAttribution: true };

// Default container styles
const defaultContainerStyle = {
  height: '800px',
  width: '100%',
  position: 'relative',
  border: '1px solid #ccc',
};

// Default ReactFlow options - optimized for better canvas fitting
const defaultReactFlowOptions = {
  zoomOnScroll: true,
  zoomOnPinch: true,
  zoomOnDoubleClick: true,
  panOnScroll: false,
  panOnScrollMode: "free",
  minZoom: 0.05, // Allow more zoom out to see entire flow
  maxZoom: 2.0,  // Allow more zoom in for details
  fitView: true, // Enable automatic fitting
  attributionPosition: 'bottom-left',
};

// Default fit view options - improved for better canvas fitting
const defaultFitViewOptions = {
  padding: 0.2, // Increased padding for better margins
  includeHiddenNodes: false,
  duration: 300,
  minZoom: 0.1,
  maxZoom: 1.2 // Prevent excessive zoom-in
};

// Styled components for nodes
const TurboNodeWrapper = styled('div', ({ $theme }) => ({
  borderRadius: '10px',
  display: 'flex',
  minHeight: '70px',
  minWidth: '150px',
  fontFamily: 'Fira Mono, monospace',
  fontWeight: 500,
  letterSpacing: '-0.2px',
  transition: 'height 0.2s ease',
  overflow: 'visible',
  backgroundColor: $theme.colors.accent100,
  color: $theme.colors.contentPrimary,
  position: 'relative',
  '::before': {
    content: '""',
    position: 'absolute',
    top: '-4px',
    left: '-4px',
    right: '-4px',
    bottom: '-4px',
    background: `conic-gradient(from -50deg at 50% 50%,${$theme.colors.warning300},${$theme.colors.accent200},${$theme.colors.accent200},${$theme.colors.warning300})`,
    borderRadius: '10px',
    zIndex: -1,
    animation: 'spin-border 4s linear infinite',
  },
}));

const Wrapper = styled('div', {
  overflow: 'hidden',
  display: 'flex',
  padding: '2px',
  position: 'relative',
  borderRadius: '10px',
  flexGrow: 1,
});

const Inner = styled('div', ({ $theme }) => ({
  background: $theme.colors.accent100,
  padding: '16px 20px',
  borderRadius: '10px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flexGrow: 1,
  position: 'relative',
}));

const Title = styled('div', {
  fontSize: '16px',
  marginBottom: '2px',
  lineHeight: 1,
});

// Custom Node Component - Memoized for performance
const CustomNode = React.memo(({ data, showAccordion = true }) => {
  const [css] = useStyletron();

  // Memoize expensive YAML processing
  const { formattedYaml, highlighted, hasContent } = useMemo(() => {
    const hasContent = data.details && Object.keys(data.details).length > 0;
    if (!hasContent || !showAccordion) {
      return { formattedYaml: '', highlighted: '', hasContent: false };
    }
    
    const formattedYaml = yaml.dump(data.details || {});
    const highlighted = Prism.highlight(formattedYaml, Prism.languages.yaml, 'yaml');
    return { formattedYaml, highlighted, hasContent };
  }, [data.details, showAccordion]);

  return (
    <TurboNodeWrapper>
      <Wrapper>
        <Inner>
          <div className="body">
            <div>
              <Title>{data.label}</Title>
            </div>
          </div>
          {showAccordion && hasContent && (
            <Accordion>
              <Panel
                overrides={{
                  Header: {
                    style: {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderTopLeftRadius: '10px',
                      borderTopRightRadius: '10px',
                    },
                  },
                  Content: {
                    style: {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderBottomLeftRadius: '10px',
                      borderBottomRightRadius: '10px',
                    },
                  },
                  ToggleIcon: {
                    component: () => (
                      <div style={{ padding:"5px", fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FiInfo size={28} />
                      </div>
                    ),
                  },
                }}
                title="parameters">
                <pre style={{ fontSize: '15px', whiteSpace: 'pre-wrap' }}>
                  <code dangerouslySetInnerHTML={{ __html: highlighted }} />
                </pre>
              </Panel>
            </Accordion>
          )}
          <Handle type="target" position={Position.Top} />
          <Handle type="source" position={Position.Bottom} />
        </Inner>
      </Wrapper>
    </TurboNodeWrapper>
  );
});

// Parse strategies
const PARSE_STRATEGIES = {
  PIPELINE_STEPS: 'pipeline_steps', // Uses usePipelineSteps hook
  DIRECT_YAML: 'direct_yaml'        // Direct YAML parsing
};

function PipelineFlowComponent({ 
  yamlText, 
  parseStrategy = PARSE_STRATEGIES.PIPELINE_STEPS,
  containerStyle = {},
  reactFlowOptions = {},
  fitViewOptions = {},
  showNodeAccordion = true,
  enableAutoFit = true,
  nodeSpacing = { nodesep: 150, ranksep: 100 }
}) {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [hasInitialFit, setHasInitialFit] = useState(false);
  const { fitView } = useReactFlow();

  // Only call the hook when using PIPELINE_STEPS strategy to avoid unnecessary processing
  const shouldUsePipelineSteps = parseStrategy === PARSE_STRATEGIES.PIPELINE_STEPS;
  const pipelineStepsData = usePipelineSteps(shouldUsePipelineSteps ? yamlText : '');
  const { dataSrcs, filters, sortedFilters, exporters } = shouldUsePipelineSteps 
    ? pipelineStepsData 
    : { dataSrcs: [], filters: {}, sortedFilters: [], exporters: [] };

  // Memoized custom node types to prevent recreation on every render
  const nodeTypes = useMemo(() => ({ 
    custom: (props) => <CustomNode {...props} showAccordion={showNodeAccordion} />
  }), [showNodeAccordion]);


  // Remove the layout trigger completely - it's causing infinite loops
  // The main layout effect will handle updates based on data changes directly

  // Memoized styles for performance (moved up to fix initialization order)
  const finalContainerStyle = useMemo(() => ({ 
    ...defaultContainerStyle, 
    ...containerStyle 
  }), [containerStyle]);
  
  const finalReactFlowOptions = useMemo(() => ({ 
    ...defaultReactFlowOptions, 
    ...reactFlowOptions 
  }), [reactFlowOptions]);

  // Simplified fit view options - static configuration
  const memoizedFitViewOptions = useMemo(() => ({
    ...defaultFitViewOptions,
    ...fitViewOptions
  }), [fitViewOptions]);

  // Simplified auto-fit - only on initial load, prevent loops
  useEffect(() => {
    if (enableAutoFit && nodes.length > 0 && !hasInitialFit) {
      // Simple, direct fit without complex scheduling
      const performFit = () => {
        fitView(memoizedFitViewOptions);
        setHasInitialFit(true);
      };

      // Use simple requestAnimationFrame to avoid scheduling conflicts
      const handle = requestAnimationFrame(performFit);
      return () => cancelAnimationFrame(handle);
    }
  }, [nodes.length, enableAutoFit, hasInitialFit]); // Removed fitView and memoizedFitViewOptions to prevent loops


  // Reset fit state when yamlText changes
  useEffect(() => {
    setHasInitialFit(false);
  }, [yamlText]);


  // Handle node position changes
  const onNodesChange = useCallback(
    (changes) => {
      setNodes((nds) =>
        nds.map((node) => {
          const change = changes.find((change) => change.id === node.id);
          return change?.type === 'position' && change.position
            ? { ...node, position: change.position }
            : node;
        })
      );
    },
    []
  );

  // Memoized YAML parsing for direct strategy
  const directYamlData = useMemo(() => {
    if (parseStrategy !== PARSE_STRATEGIES.DIRECT_YAML || !yamlText) {
      return { tempNodes: [], tempEdges: [] };
    }

    try {
      const parsed = yaml.load(yamlText, { schema: CUSTOM_SCHEMA });
      const tempNodes = [];
      const tempEdges = [];

      // Parse data sources
      Object.entries(parsed.data_sources || {}).forEach(([key, value]) => {
        tempNodes.push({ 
          id: key, 
          type: 'custom', 
          data: { label: key, details: value } 
        });
      });

      // Parse pipelines and filters
      Object.values(parsed.pipelines || {}).forEach(pipeline => {
        Object.entries(pipeline.filters || {}).forEach(([filterId, filter]) => {
          tempNodes.push({ 
            id: filterId, 
            type: 'custom', 
            data: { label: `${filterId} (${filter.type})`, details: filter } 
          });

          (filter.depends_on || []).forEach(dep => {
            tempEdges.push({ 
              id: `e-${dep}-${filterId}`, 
              source: dep, 
              target: filterId, 
              animated: true, 
              style: { stroke: '#999', strokeWidth: 2 } 
            });
          });
        });
      });

      // Parse exporters
      Object.entries(parsed.exporters || {}).forEach(([key, value]) => {
        tempNodes.push({ 
          id: key, 
          type: 'custom', 
          data: { label: `${key} (${value.type})`, details: value } 
        });

        // Connect all non-raw nodes to exporters
        tempNodes.filter(n => !n.id.startsWith('motorbike_raw')).forEach(n => {
          tempEdges.push({ 
            id: `e-${n.id}-${key}`, 
            source: n.id, 
            target: key, 
            animated: true, 
            style: { stroke: '#999', strokeWidth: 2 } 
          });
        });
      });

      return { tempNodes, tempEdges };
    } catch (error) {
      console.error('Direct YAML parsing error:', error);
      return { tempNodes: [], tempEdges: [] };
    }
  }, [parseStrategy, yamlText]);

  // Memoized pipeline steps data
  const pipelineStepsNodesData = useMemo(() => {
    if (parseStrategy !== PARSE_STRATEGIES.PIPELINE_STEPS) {
      return { tempNodes: [], tempEdges: [] };
    }

    const tempNodes = [];
    const tempEdges = [];

    // Add data sources
    dataSrcs.forEach((key) => {
      tempNodes.push({
        id: key,
        type: 'custom',
        data: { label: key, details: {} },
      });
    });

    // Add filters
    sortedFilters.forEach((filterId) => {
      const filter = filters[filterId];
      tempNodes.push({
        id: filterId,
        type: 'custom',
        data: { label: `${filterId} (${filter.type})`, details: filter },
      });

      (filter.depends_on || []).forEach((dep) => {
        tempEdges.push({
          id: `e-${dep}-${filterId}`,
          source: dep,
          target: filterId,
          animated: true,
          style: { stroke: '#999', strokeWidth: 2 },
        });
      });
    });

    // Add exporters
    exporters.forEach((key) => {
      tempNodes.push({
        id: key,
        type: 'custom',
        data: { label: `${key} (exporter)`, details: {} },
      });

      sortedFilters.forEach((fId) => {
        tempEdges.push({
          id: `e-${fId}-${key}`,
          source: fId,
          target: key,
          animated: true,
          style: { stroke: '#999', strokeWidth: 2 },
        });
      });
    });

    return { tempNodes, tempEdges };
  }, [parseStrategy, dataSrcs, filters, sortedFilters, exporters]);

  // Use refs to store the last processed values and prevent loops
  const lastYamlRef = useRef('');
  const lastStrategyRef = useRef('');
  const isProcessingRef = useRef(false);

  // Main layout effect - completely isolated to prevent infinite loops
  useEffect(() => {
    // Only process if yamlText or parseStrategy actually changed
    if (
      isProcessingRef.current || 
      (lastYamlRef.current === yamlText && lastStrategyRef.current === parseStrategy)
    ) {
      return;
    }

    // Update refs to track what we're processing
    lastYamlRef.current = yamlText;
    lastStrategyRef.current = parseStrategy;
    isProcessingRef.current = true;

    if (!yamlText) {
      setNodes([]);
      setEdges([]);
      isProcessingRef.current = false;
      return;
    }

    // Get the appropriate data based on strategy (computed here to avoid memoization issues)
    let tempNodes = [];
    let tempEdges = [];

    if (parseStrategy === PARSE_STRATEGIES.DIRECT_YAML) {
      const result = directYamlData;
      tempNodes = result.tempNodes;
      tempEdges = result.tempEdges;
    } else {
      const result = pipelineStepsNodesData;
      tempNodes = result.tempNodes;
      tempEdges = result.tempEdges;
    }

    if (tempNodes.length === 0) {
      setNodes([]);
      setEdges([]);
      isProcessingRef.current = false;
      return;
    }

    // Use a longer timeout to ensure everything settles
    const timeoutId = setTimeout(() => {
      import('dagre').then(({ default: dagre }) => {
        try {
          const g = new dagre.graphlib.Graph()
            .setDefaultEdgeLabel(() => ({}))
            .setGraph({ 
              rankdir: 'TB', 
              nodesep: Math.max(nodeSpacing.nodesep, 80),
              ranksep: Math.max(nodeSpacing.ranksep, 120),
              marginx: 40,
              marginy: 40,
              align: 'UL'
            });

          const nodeWidth = 180;
          const nodeHeight = 100;

          const ensureNode = (id) => {
            if (!g.node(id)) {
              g.setNode(id, { width: nodeWidth, height: nodeHeight });
            }
          };

          tempNodes.forEach(node => ensureNode(node.id));
          tempEdges.forEach(edge => {
            ensureNode(edge.source);
            ensureNode(edge.target);
            g.setEdge(edge.source, edge.target);
          });

          dagre.layout(g);

          const layoutedNodes = tempNodes.map((node) => {
            const layout = g.node(node.id);
            return {
              ...node,
              targetPosition: 'left',
              sourcePosition: 'right',
              position: {
                x: layout.x - nodeWidth / 2,
                y: layout.y - nodeHeight / 2,
              },
            };
          });

          // Only update if we're still processing the same data
          if (lastYamlRef.current === yamlText && lastStrategyRef.current === parseStrategy) {
            setNodes(layoutedNodes);
            setEdges(tempEdges);
          }
        } catch (error) {
          console.error('Layout error:', error);
        } finally {
          isProcessingRef.current = false;
        }
      }).catch((error) => {
        console.error('Dagre import error:', error);
        isProcessingRef.current = false;
      });
    }, 200); // Longer delay

    return () => {
      clearTimeout(timeoutId);
      isProcessingRef.current = false;
    };
  }, [yamlText, parseStrategy]); // Only depend on the core values that should trigger updates

  return (
    <Block $style={finalContainerStyle}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={() => {}}
        onConnect={() => {}}
        proOptions={proOptions}
        {...finalReactFlowOptions}
        fitViewOptions={memoizedFitViewOptions}
              >
          <Controls 
            showFitView={true}
            showZoom={true}
            showInteractive={true}
            fitViewOptions={memoizedFitViewOptions}
          />
          <Background 
            variant="dots"
            gap={20}
            size={1}
            color="#e2e2e2"
          />
        </ReactFlow>
    </Block>
  );
}

// Main export component with provider
export default function PipelineFlowWithProvider({ 
  yamlText, 
  parseStrategy,
  containerStyle,
  reactFlowOptions,
  fitViewOptions,
  showNodeAccordion,
  enableAutoFit,
  nodeSpacing
}) {
  return (
    <ReactFlowProvider>
      <PipelineFlowComponent 
        yamlText={yamlText}
        parseStrategy={parseStrategy}
        containerStyle={containerStyle}
        reactFlowOptions={reactFlowOptions}
        fitViewOptions={fitViewOptions}
        showNodeAccordion={showNodeAccordion}
        enableAutoFit={enableAutoFit}
        nodeSpacing={nodeSpacing}
      />
    </ReactFlowProvider>
  );
}

// Export the parse strategies for convenience
export { PARSE_STRATEGIES }; 